import { colors, semanticColors, semanticColorsDark } from './theme.colors';
import { fontWeight, letterSpacing } from './theme.typography';

export const breakpoints = {
  xs: '320px',
  sm: '360px',
  maxMobile: '1023px',
  md: '1024px',
  lg: '1024px',
  maxTablet: '1139px',
  xl: '1920px',
};

export const spacing = {
  xs4: '2px',
  xs3: '4px',
  xs2: '8px',
  xs: '12px',
  sm: '16px',
  md: '24px',
  lg: '32px',
  xl: '40px',
  xl2: '56px',
  xl3: '80px',
};

export const dimension = {
  xs2: '8px',
  xs: '16px',
  sm: '24px',
  md: '32px',
  lg: '40px',
  xl: '48px',
  xl2: '56px',
  xl3: '64px',
  xl4: '72px',
  xl5: '80px',
  xl6: '88px',
  xl7: '96px',
};

export const radius = {
  xs2: '4px',
  xs: '8px',
  sm: '12px',
  md: '16px',
  lg: '20px',
  xl: '24px',
  xl2: '28px',
};

const blur = {
  light: '2px',
  med: '8px',
  bold: '16px',
  strong: '24px',
};

export const shadows = {
  bottom: `0px 1px ${blur.light} ${semanticColors.effect.shadowSoft}`,
  top: `0px -1px ${blur.light} ${semanticColors.effect.shadowSoft}`,
  sm: `0px 0px ${blur.med} ${semanticColors.effect.shadowSoft}`,
  md: `0px 0px ${blur.bold} ${semanticColors.effect.shadowStrong}`,
  lg: `0px 0px ${blur.strong} ${semanticColors.effect.shadowStrong}`,
};

export const stroke = {
  light: '1px',
  med: '2px',
  bold: '4px',
};

export const resolver = () => ({
  // README: check Mantine variables list https://mantine.dev/styles/css-variables-list/ before adding here
  variables: {
    '--dimension-xs2': dimension.xs2,
    '--dimension-xs': dimension.xs,
    '--dimension-sm': dimension.sm,
    '--dimension-md': dimension.md,
    '--dimension-lg': dimension.lg,
    '--dimension-xl': dimension.xl,
    '--dimension-xl2': dimension.xl2,
    '--dimension-xl3': dimension.xl3,
    '--dimension-xl4': dimension.xl4,
    '--dimension-xl5': dimension.xl5,
    '--dimension-xl6': dimension.xl6,
    '--dimension-xl7': dimension.xl7,
    '--font-weight-regular': fontWeight.regular,
    '--font-weight-bold': fontWeight.bold,
    '--font-weight-medium': fontWeight.medium,
    '--font-weight-book': fontWeight.book,
    '--letterspacing-tighter': letterSpacing.tighter,
    '--letterspacing-tight': letterSpacing.tight,
    '--letterspacing-normal': letterSpacing.normal,
    '--letterspacing-wide': letterSpacing.wide,
    '--color-graph-blue': colors.graph.blue,
    '--color-graph-indigo': colors.graph.indigo,
    '--color-graph-green': colors.graph.green,
    '--color-graph-yellow': colors.graph.yellow,
    '--color-graph-pink': colors.graph.pink,
    '--color-graph-gray': colors.graph.gray,
    '--effect-modal-overlay': semanticColors.effect.modalOverlay,
    '--color-neutral-8': colors.neutral.x8,
    '--radius-xl2': radius.xl2,
  },
  light: {
    '--border-primary': semanticColors.border.primary,
    '--border-secondary': semanticColors.border.secondary,
    '--border-tertiary': semanticColors.border.tertiary,
    '--border-active': semanticColors.border.active,
    '--border-caution': semanticColors.border.caution,
    '--border-inverse-primary': semanticColors.border.inversePrimary,
    '--border-inverse-secondary': semanticColors.border.inverseSecondary,
    '--border-inverse-tertiary': semanticColors.border.inverseTertiary,
    '--border-inverse-caution': semanticColors.border.inverseCaution,
    '--content-primary': semanticColors.content.primary,
    '--content-secondary': semanticColors.content.secondary,
    '--content-tertiary': semanticColors.content.tertiary,
    '--content-accent-primary': semanticColors.content.accentPrimary,
    '--content-accent-secondary': semanticColors.content.accentSecondary,
    '--content-active': semanticColors.content.active,
    '--content-success': semanticColors.content.success,
    '--content-caution': semanticColors.content.caution,
    '--content-inverse-primary': semanticColors.content.inversePrimary,
    '--content-inverse-secondary': semanticColors.content.inverseSecondary,
    '--content-inverse-active': semanticColors.content.inverseActive,
    '--content-inverse-success': semanticColors.content.inverseSuccess,
    '--content-inverse-caution': semanticColors.content.inverseCaution,
    '--surface-primary': semanticColors.surface.primary,
    '--surface-secondary': semanticColors.surface.secondary,
    '--surface-tertiary': semanticColors.surface.tertiary,
    '--surface-accent-primary': semanticColors.surface.accentPrimary,
    '--surface-accent-secondary': semanticColors.surface.accentSecondary,
    '--surface-active': semanticColors.surface.active,
    '--surface-success': semanticColors.surface.success,
    '--surface-caution': semanticColors.surface.caution,
    '--surface-inverse-primary': semanticColors.surface.inversePrimary,
    '--surface-inverse-secondary': semanticColors.surface.inverseSecondary,
    '--surface-inverse-tertiary': semanticColors.surface.inverseTertiary,
    '--surface-paper-1': semanticColors.surface.paper.x1,
    '--surface-paper-2': semanticColors.surface.paper.x2,
    '--surface-paper-3': semanticColors.surface.paper.x3,
    '--surface-paper-4': semanticColors.surface.paper.x4,
    '--effect-pressed-overlay': semanticColors.effect.pressedOverlay,
    '--effect-inverse-pressed-overlay': semanticColors.effect.inversePressedOverlay,
    // Mantine Overrides
    '--mantine-color-text': semanticColors.content.primary,
    '--mantine-color-body': semanticColors.surface.secondary,
    '--mantine-color-error': semanticColors.content.caution,
  },
  dark: {
    '--border-primary': semanticColorsDark.border.primary,
    '--border-secondary': semanticColorsDark.border.secondary,
    '--border-tertiary': semanticColorsDark.border.tertiary,
    '--border-active': semanticColorsDark.border.active,
    '--border-caution': semanticColorsDark.border.caution,
    '--border-inverse-primary': semanticColorsDark.border.inversePrimary,
    '--border-inverse-secondary': semanticColorsDark.border.inverseSecondary,
    '--border-inverse-tertiary': semanticColorsDark.border.inverseTertiary,
    '--border-inverse-caution': semanticColorsDark.border.inverseCaution,
    '--content-primary': semanticColorsDark.content.primary,
    '--content-secondary': semanticColorsDark.content.secondary,
    '--content-tertiary': semanticColorsDark.content.tertiary,
    '--content-accent-primary': semanticColorsDark.content.accentPrimary,
    '--content-accent-secondary': semanticColorsDark.content.accentSecondary,
    '--content-active': semanticColorsDark.content.active,
    '--content-success': semanticColorsDark.content.success,
    '--content-caution': semanticColorsDark.content.caution,
    '--content-inverse-primary': semanticColorsDark.content.inversePrimary,
    '--content-inverse-secondary': semanticColorsDark.content.inverseSecondary,
    '--content-inverse-active': semanticColorsDark.content.inverseActive,
    '--content-inverse-success': semanticColorsDark.content.inverseSuccess,
    '--content-inverse-caution': semanticColorsDark.content.inverseCaution,
    '--surface-primary': semanticColorsDark.surface.primary,
    '--surface-secondary': semanticColorsDark.surface.secondary,
    '--surface-tertiary': semanticColorsDark.surface.tertiary,
    '--surface-accent-primary': semanticColorsDark.surface.accentPrimary,
    '--surface-accent-secondary': semanticColorsDark.surface.accentSecondary,
    '--surface-active': semanticColorsDark.surface.active,
    '--surface-success': semanticColorsDark.surface.success,
    '--surface-caution': semanticColorsDark.surface.caution,
    '--surface-inverse-primary': semanticColorsDark.surface.inversePrimary,
    '--surface-inverse-secondary': semanticColorsDark.surface.inverseSecondary,
    '--surface-inverse-tertiary': semanticColorsDark.surface.inverseTertiary,
    '--surface-paper-1': semanticColorsDark.surface.paper.x1,
    '--surface-paper-2': semanticColorsDark.surface.paper.x2,
    '--surface-paper-3': semanticColorsDark.surface.paper.x3,
    '--surface-paper-4': semanticColorsDark.surface.paper.x4,
    '--effect-pressed-overlay': semanticColorsDark.effect.pressedOverlay,
    '--effect-inverse-pressed-overlay': semanticColorsDark.effect.inversePressedOverlay,
    // Mantine Overrides
    '--mantine-color-text': semanticColorsDark.content.primary,
    '--mantine-color-body': semanticColorsDark.surface.secondary,
    '--mantine-color-error': semanticColorsDark.content.caution,
  },
});
